import eventNames from '@core/utils/constants';

class VBusDirective {
  constructor(eventBus) {
    this.eventBus = eventBus;
    this.RESERVED_MODIFIERS = ['submit', 'prevent', 'stop', 'click'];
  }

  mounted(targetElement, binding) {
    const path = this.extractPath(binding);
    if (!path) return;

    const eventName = this.resolveEventName(path);
    if (!eventName) return;

    const domEvent = this.resolveDomEvent(targetElement, binding);
    const handler = this.buildHandler(targetElement, binding, eventName);

    this.attach(targetElement, domEvent, handler);
  }

  unmounted(targetElement) {
    this.detach(targetElement);
  }

  extractPath(binding) {
    const parts = [];
    if (binding?.arg) parts.push(binding.arg);

    const modifiers = binding?.modifiers ?? {};
    for (const modifier of Object.keys(modifiers)) {
      if (!this.RESERVED_MODIFIERS.includes(modifier)) {
        parts.push(modifier);
      }
    }

    if (parts.length === 0) return null;
    return parts.join('.');
  }

  resolveEventName(path) {
    const segments = path.split('.');
    let current = eventNames;

    for (const segment of segments) {
      if (current?.[segment] === undefined) return null;
      current = current[segment];
    }

    return typeof current === 'string' ? current : null;
  }

  resolveDomEvent(targetElement, binding) {
    const isForm = targetElement.tagName === 'FORM';
    const modifiers = binding?.modifiers ?? {};

    if (modifiers.submit) return 'submit';
    if (modifiers.click) return 'click';
    if (isForm) return 'submit';

    return 'click';
  }

  buildHandler(targetElement, binding, eventName) {
    const isFormSubmission = this.isFormSubmitCase(targetElement, binding);
    const shouldPrevent = Boolean(binding?.modifiers?.prevent);
    const shouldStop = Boolean(binding?.modifiers?.stop);

    return (domEvent) => {
      if (shouldPrevent) domEvent.preventDefault();
      if (shouldStop) domEvent.stopPropagation();

      const payload = isFormSubmission
        ? this.extractFormData(domEvent.target)
        : {};

      this.eventBus.emit(eventName, payload);
    };
  }

  isFormSubmitCase(targetElement, binding) {
    const isForm = targetElement.tagName === 'FORM';
    const hasSubmitModifier = Boolean(binding?.modifiers?.submit);
    return isForm && hasSubmitModifier;
  }

  extractFormData(formElement) {
    const data = new FormData(formElement);
    return Object.fromEntries(data.entries());
  }

  attach(targetElement, domEvent, handler) {
    targetElement.addEventListener(domEvent, handler);
    targetElement._vBusHandler = { domEvent, handler };
  }

  detach(targetElement) {
    const cached = targetElement._vBusHandler;
    if (!cached) return;

    const { domEvent, handler } = cached;
    targetElement.removeEventListener(domEvent, handler);
    delete targetElement._vBusHandler;
  }
}

function createVBusDirective(eventBus) {
  const instance = new VBusDirective(eventBus);

  return {
    mounted: instance.mounted.bind(instance),
    unmounted: instance.unmounted.bind(instance)
  };
}

export { VBusDirective, createVBusDirective };
